import type { DataAdapter } from "@veiligh/reporting-core/adapters";
import { mapDashboardWidgets, mapWidgetList } from "@veiligh/reporting-core/ui";
import type {
  GetCollectionRequest,
  UpdateWidgetPositionRequest,
  CreateUserDashboardRequest,
  UpdateUserDashboardRequest,
  ShareDashboardRequest,
  CreateWidgetConfigRequest,
  UpdateWidgetConfigRequest,
  BulkDeleteRequest,
  CloneDashboardRequest,
  CreateQueryRequest,
} from "@veiligh/reporting-core/adapters";
import type {
  UserDashboard,
  Widget,
  WidgetConfiguration,
  PaginatedDashboardTemplatesResponse,
  DashboardTemplatesQueryParams,
  DashboardTemplate,
} from "@veiligh/reporting-core/domain";
import type {
  SavedQuery,
  SavedQueryById,
  QueryField,
  QueryStore,
  RunQueryData,
  ParameterType,
} from "@veiligh/reporting-core/domain/query/types";
import { DEFAULT_API_PAYLOAD } from "~/lib/constants";
import { restApi } from "~/redux/restApi";
import store from "~/redux/store";

type TemplateQueryLike = {
  id: string;
  displayName?: string | null;
  serializedDefinition?: string | null;
  previewQuery?: unknown;
};

type ConvertTemplateQueriesOptions = {
  existingNames?: string[];
  fallbackName?: string;
};

interface TemplateQueryTenantDto {
  templateQueryId: string;
  request: CreateQueryRequest;
}

type DashboardTemplateWithQueries = DashboardTemplate & {
  templateQueries?: TemplateQueryLike[] | null;
};

const convertTemplateQueriesToTenantDtos = (
  templateQueries: TemplateQueryLike[],
  { existingNames = [], fallbackName }: ConvertTemplateQueriesOptions = {},
): TemplateQueryTenantDto[] => {
  const takenNames = new Set(
    existingNames
      .map((name) => name?.trim())
      .filter((name): name is string => Boolean(name))
      .map((name) => name.toLowerCase()),
  );

  const fallbackBase = fallbackName?.trim() || "Template Query";

  const ensureUniqueName = (base: string) => {
    let candidate = base;
    let suffix = 1;

    while (takenNames.has(candidate.toLowerCase())) {
      suffix += 1;
      candidate = `${base} (${suffix})`;
    }

    takenNames.add(candidate.toLowerCase());
    return candidate;
  };

  const parseDefinition = (templateQuery: TemplateQueryLike) => {
    if (templateQuery.serializedDefinition) {
      try {
        return JSON.parse(templateQuery.serializedDefinition);
      } catch (error) {
        console.warn(
          "Failed to parse template query definition, falling back to raw string",
          error,
        );
        return templateQuery.serializedDefinition;
      }
    }

    if (templateQuery.previewQuery) {
      return templateQuery.previewQuery;
    }

    return {};
  };

  return templateQueries.map((templateQuery, index) => {
    const preferredName = templateQuery.displayName?.trim();
    const baseName =
      preferredName && preferredName.length > 0
        ? preferredName
        : `${fallbackBase} ${index + 1}`;
    const uniqueName = ensureUniqueName(baseName);

    return {
      templateQueryId: templateQuery.id,
      request: {
        name: uniqueName,
        query: parseDefinition(templateQuery),
      },
    };
  });
};

/**
 * Web app implementation of DataAdapter
 * Uses the existing restApi hooks to communicate with the backend
 */
export class WebReportingAdapter implements DataAdapter {
  // ===== Dashboard operations =====

  async getUserDashboards(): Promise<UserDashboard[]> {
    const action = restApi.endpoints.getUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    const data = result?.data?.result ?? [];
    return data.map(mapDashboardWidgets);
  }

  async getUserDashboard(id: string): Promise<UserDashboard> {
    const action = restApi.endpoints.getApiV1UserDashboardByUserDashboardId.initiate({
      userDashboardId: id,
      ...DEFAULT_API_PAYLOAD,
    } as any);
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async getUserDashboardPaginated(params: {
    page: number;
    pageSize: number;
    searchQuery: string;
  }): Promise<{ result: UserDashboard[]; totalCount: number }> {
    const action = restApi.endpoints.getUserDashboardPaginated.initiate({
      ...DEFAULT_API_PAYLOAD,
      page: params.page,
      pageSize: params.pageSize,
      searchQuery: params.searchQuery,
    });
    const response = await store.dispatch(action as any);
    const payload = response?.data?.result;
    const dashboards = payload?.data ?? [];

    return {
      result: dashboards.map(mapDashboardWidgets),
      totalCount: payload?.totalRecordCount ?? 0,
    };
  }

  async createUserDashboard(
    data: CreateUserDashboardRequest & { pinned?: boolean; userIds?: string[] },
  ): Promise<UserDashboard> {
    const action = restApi.endpoints.createUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      createUserDashboardRequest: data as any,
    });
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async updateUserDashboard(
    id: string,
    data: UpdateUserDashboardRequest & { pinned?: boolean; userIds?: string[] },
  ): Promise<UserDashboard> {
    const action = restApi.endpoints.updateUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      userDashboardId: id,
      updateUserDashboardRequest: data as any,
    });
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async deleteUserDashboard(id: string): Promise<void> {
    const action = restApi.endpoints.deleteUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      userDashboardId: id,
    });
    await store.dispatch(action as any);
  }

  async bulkDeleteDashboards(request: BulkDeleteRequest): Promise<void> {
    const action = restApi.endpoints.deleteUserDashboardBulk.initiate({
      ...DEFAULT_API_PAYLOAD,
      bulkDeleteDashboardRequest: { ids: request.ids },
    });
    await store.dispatch(action as any);
  }

  async toggleDashboardPinning(id: string): Promise<UserDashboard> {
    const action = restApi.endpoints.toggleDashboardPinning.initiate({
      ...DEFAULT_API_PAYLOAD,
      userDashboardId: id,
    });
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async cloneDashboard(request: CloneDashboardRequest): Promise<UserDashboard> {
    const action = restApi.endpoints.cloneUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      dashboardId: request.dashboardId,
      title: request.title,
    });
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async shareDashboard(id: string, data: ShareDashboardRequest): Promise<void> {
    const action = restApi.endpoints.shareDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      dashboardId: id,
      shareDashboardRequest: data as any,
    });
    await store.dispatch(action as any);
  }

  async updateDashboardPositions(
    positions: UpdateWidgetPositionRequest[],
  ): Promise<void> {
    const action =
      restApi.endpoints.updateDashboardPositionConfiguration.initiate({
        ...DEFAULT_API_PAYLOAD,
        body: positions,
      });
    const result = await store.dispatch(action as any);
    if (result?.error) {
      throw new Error(
        result.error?.data?.message || "Failed to update positions",
      );
    }
  }

  async getUsers(): Promise<
    Array<{ id: string; name: string; email?: string }>
  > {
    const action = restApi.endpoints.getUsers.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  getCurrentUserId(): string {
    try {
      const state = store.getState();
      return state?.persistedReducer?.auth?.userId || "";
    } catch {
      return "";
    }
  }

  // ===== Widget operations =====

  async getWidgets(): Promise<Widget[]> {
    const action = restApi.endpoints.getWidgets.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    const widgets = result?.data?.result ?? [];
    return mapWidgetList(widgets);
  }

  async createWidgetConfiguration(
    data: CreateWidgetConfigRequest,
  ): Promise<WidgetConfiguration> {
    const action = restApi.endpoints.postWidgetConfiguration.initiate({
      ...DEFAULT_API_PAYLOAD,
      createWidgetConfigurationRequest: data as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result;
  }

  async updateWidgetConfiguration(
    id: string,
    data: UpdateWidgetConfigRequest,
  ): Promise<WidgetConfiguration> {
    const action = restApi.endpoints.updateWidgetConfiguration.initiate({
      ...DEFAULT_API_PAYLOAD,
      widgetConfigurationId: id,
      updateWidgetConfigurationRequest: data as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result;
  }

  async deleteWidgetConfiguration(id: string): Promise<void> {
    const action = restApi.endpoints.deleteWidgetConfig.initiate({
      ...DEFAULT_API_PAYLOAD,
      widgetConfigurationId: id,
    });
    await store.dispatch(action as any);
  }

  // ===== Query operations =====

  async getQueries(params?: {
    searchQuery?: string;
    includeCloned?: boolean;
  }): Promise<SavedQuery[]> {
    const action = restApi.endpoints.getQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      ...(params?.searchQuery ? { searchQuery: params.searchQuery } : {}),
      ...(params?.includeCloned !== undefined
        ? { includeCloned: params.includeCloned }
        : {}),
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getQueryById(id: string): Promise<SavedQueryById> {
    const action = restApi.endpoints.getSavedByQueryId.initiate({
      ...DEFAULT_API_PAYLOAD,
      queryId: id,
    });
    const result = await store.dispatch(action as any);
    return result?.data;
  }

  async createQuery(data: CreateQueryRequest): Promise<SavedQuery> {
    const action = restApi.endpoints.saveQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      createQueryRequest: data,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result;
  }

  updateQuery(id: string, data: CreateQueryRequest): Promise<SavedQuery>;
  updateQuery(payload: any): Promise<void>;
  async updateQuery(
    idOrPayload: string | any,
    dataOrUndefined?: CreateQueryRequest,
  ): Promise<SavedQuery | void> {
    if (typeof idOrPayload === "string" && dataOrUndefined) {
      // First overload: updateQuery(id, data) - returns SavedQuery
      const action = restApi.endpoints.updateSavedQueryById.initiate({
        ...DEFAULT_API_PAYLOAD,
        queryId: idOrPayload,
        createQueryRequest: dataOrUndefined as CreateQueryRequest,
      });
      const result = await store.dispatch(action as any);
      return result?.data?.result;
    } else {
      // Second overload: updateQuery(payload) - returns void (legacy)
      const action = restApi.endpoints.updateSavedQueryById.initiate({
        ...DEFAULT_API_PAYLOAD,
        queryId: idOrPayload.id,
        createQueryRequest: idOrPayload,
      });
      await store.dispatch(action as any);
    }
  }

  async deleteQuery(id: string): Promise<void> {
    const action = restApi.endpoints.deleteQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      deleteQueryRequest: { queryIds: [id] },
    });
    await store.dispatch(action as any);
  }

  async bulkDeleteQueries(ids: string[]): Promise<void> {
    const action = restApi.endpoints.deleteQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      deleteQueryRequest: { queryIds: ids },
    });
    await store.dispatch(action as any);
  }

  async runQuery(query: any): Promise<RunQueryData[]> {
    const action = restApi.endpoints.runQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      queryNodeDto: query,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  // ===== Query schema operations =====

  async getScreens(): Promise<string[]> {
    const action = restApi.endpoints.getScreens.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getQueryItemTypes(): Promise<string[]> {
    const action = restApi.endpoints.getV2Query.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getQueryFieldsByType(parameterType: string): Promise<any[]> {
    const action = restApi.endpoints.getQuery.initiate({
      ...DEFAULT_API_PAYLOAD,
      parameterType,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getCollectionForField(
    screen: string,
    field: string,
  ): Promise<Array<{ id: string; name: string }>> {
    const action = restApi.endpoints.getCollections.initiate({
      ...DEFAULT_API_PAYLOAD,
      getCollectionRequest: { screen, types: [field] } as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  // ===== Collection operations =====

  async getCollections(request: GetCollectionRequest): Promise<any> {
    const action = restApi.endpoints.getCollections.initiate({
      "App-Name": DEFAULT_API_PAYLOAD["App-Name"],
      getCollectionRequest: request as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getCollectionsLink(types: string[]): Promise<any> {
    const action = restApi.endpoints.getSuggestions.initiate({
      "App-Name": DEFAULT_API_PAYLOAD["App-Name"],
      getLinkCollectionRequest: { types } as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getCollectionsGroupBy(): Promise<string[]> {
    const action = restApi.endpoints.getCollectionsGroupBy.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getGroupByQueryId(queryId: string): Promise<string[]> {
    const action = restApi.endpoints.getGroupByQueryId.initiate({
      ...DEFAULT_API_PAYLOAD,
      queryId,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getDaysSinceLast(classificationId: string): Promise<number> {
    const action = restApi.endpoints.getDaysSinceLast.initiate({
      ...DEFAULT_API_PAYLOAD,
      injuryClassificationIds: [classificationId],
    });
    const result = await store.dispatch(action as any);
    // The API returns a Record<string, number>, so we need to extract the value
    const record = result?.data?.result as Record<string, number> | undefined;
    if (record) {
      const values = Object.values(record);
      return values[0] ?? 0;
    }
    return 0;
  }

  // ===== Marketplace operations =====

  async getDashboardTemplates(
    params: DashboardTemplatesQueryParams = {},
  ): Promise<PaginatedDashboardTemplatesResponse> {
    const action = restApi.endpoints.getDashboardTemplates.initiate({
      ...DEFAULT_API_PAYLOAD,
      "published-only": params.publishedOnly,
      searchQuery: params.searchQuery,
      category: params.category,
      page: params.page,
      pageSize: params.pageSize,
    });
    const result = await store.dispatch(action as any);

    if (result?.error) {
      throw new Error(
        result.error?.data?.message || "Failed to load dashboard templates",
      );
    }

    const payload = (
      result?.data as { result?: PaginatedDashboardTemplatesResponse }
    )?.result;

    return (
      payload ?? {
        templates: [],
        totalCount: 0,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 0,
      }
    );
  }

  async installDashboardTemplate(templateId: string): Promise<void> {
    const templatesResponse = await this.getDashboardTemplates({
      page: 1,
      pageSize: 1,
    });
    const template = templatesResponse.templates.find(
      (entry) => entry.id === templateId,
    ) as DashboardTemplateWithQueries | undefined;

    if (template?.templateQueries?.length) {
      const existingQueries = await this.getQueries({ includeCloned: true });
      const existingNames = existingQueries
        .map((query) => query.name?.trim())
        .filter((name): name is string => Boolean(name));

      const templateDtos = convertTemplateQueriesToTenantDtos(
        template.templateQueries,
        {
          existingNames,
          fallbackName: template.title ? `${template.title} Query` : undefined,
        },
      );
      for (const dto of templateDtos) {
        await this.createQuery({
          name: dto.request.name,
          query: dto.request.query,
        });
      }
    }

    const action = restApi.endpoints.installDashboardTemplate.initiate({
      ...DEFAULT_API_PAYLOAD,
      templateId,
    });
    const result = await store.dispatch(action as any);

    if (result?.error) {
      throw new Error(
        result.error?.data?.message || "Failed to install dashboard template",
      );
    }

    return mapDashboardWidgets(result?.data?.result);
  }

  async getDashboardTemplateById(
    templateId: string,
  ): Promise<DashboardTemplate> {
    const action = restApi.endpoints.getDashboardTemplateById.initiate({
      ...DEFAULT_API_PAYLOAD,
      templateId,
    });

    const result = await store.dispatch(action as any);

    if (result?.error) {
      throw new Error(
        result.error?.data?.message || "Failed to load dashboard template",
      );
    }

    return (result?.data?.result ?? result?.data) as DashboardTemplate;
  }

  async uninstallDashboardTemplate(templateId: string): Promise<void> {
    const action = restApi.endpoints.uninstallDashboardTemplate.initiate({
      ...DEFAULT_API_PAYLOAD,
      templateId,
    });
    const result = await store.dispatch(action as any);

    if (result?.error) {
      throw new Error(
        result.error?.data?.message || "Failed to uninstall dashboard template",
      );
    }
  }

  // ===== Query field operations =====

  async getQueryFields(parameterType: ParameterType): Promise<QueryField[]> {
    const action = restApi.endpoints.getQuery.initiate({
      ...DEFAULT_API_PAYLOAD,
      parameterType,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getV2Query(): Promise<QueryStore> {
    const action = restApi.endpoints.getV2Query.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result;
  }

  // ===== Saved query operations (legacy support) =====

  async getSavedQueries(): Promise<SavedQuery[]> {
    return this.getQueries();
  }

  async getSavedQueryById(payload: {
    queryId: string;
  }): Promise<{ result?: SavedQueryById }> {
    const action = restApi.endpoints.getSavedByQueryId.initiate({
      ...DEFAULT_API_PAYLOAD,
      queryId: payload.queryId,
    });
    const result = await store.dispatch(action as any);
    return { result: result?.data?.result };
  }

  async saveQuery(payload: any): Promise<void> {
    await this.createQuery(payload);
  }

  async deleteQueryById(id: string): Promise<void> {
    const action = restApi.endpoints.deleteQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      deleteQueryRequest: { queryIds: [id] },
    });
    await store.dispatch(action as any);
  }

  async deleteQueryBulk(ids: string[]): Promise<void> {
    return this.bulkDeleteQueries(ids);
  }
}
